// Calcolo inverso: da un netto desiderato alla RAL da chiedere.
//
// Il netto NON e' una funzione monotona della RAL. Su ogni soglia normativa
// che si chiude di colpo - esenzione dell'addizionale comunale, trattamento
// integrativo a 15.000 di imponibile, scaglioni della somma integrativa, la
// maggiorazione di 65 euro fra 25.000 e 35.000 - il netto scende mentre il
// lordo sale. Di conseguenza alcuni netti non sono raggiungibili da nessuna
// RAL, e una bisezione (che presuppone la monotonia) restituisce risultati
// sbagliati.
//
// Qui si cerca quindi per scansione la RAL MINIMA che raggiunge il target:
// una passata grossolana per individuare la zona, poi una passata a un euro
// sulle celle candidate. tests/lordo-da-netto.test.js verifica il risultato
// contro una scansione esaustiva.
import { computeNetto } from './nettoEngine.js';

const PASSO_GROSSOLANO = 100;

// Sporgenza massima di un picco interno a una cella rispetto ai suoi estremi:
// e' limitata da PASSO_GROSSOLANO per la pendenza massima del netto, che non
// supera 1 euro per euro di RAL. Misurata a 97 euro/anno; qui si tiene un
// margine quadruplo. Serve a non saltare il picco stretto che precede una
// soglia (l'ultimo euro sotto l'esenzione comunale, per esempio).
const MARGINE_ANNUO = 4 * PASSO_GROSSOLANO;

const RAL_MASSIMA = 1000000;

// Perdita minima, in euro di netto annuo, perche' un calo conti come soglia.
// Le detrazioni che decrescono in modo continuo, arrotondate all'euro,
// producono di continuo cali da un euro: sono rumore, non soglie. I salti veri
// valgono almeno 60 euro.
const PERDITA_MINIMA = 20;

export const MODI = ['busta', 'medio', 'annuo'];

// 'busta'  netto di un mese ordinario, senza 13a/14a: la cifra sul cedolino
// 'medio'  netto annuo diviso le mensilita'
// 'annuo'  netto annuo
export function proiettaNetto(breakdown, modo = 'busta') {
  if (modo === 'annuo') return breakdown.nettoAnnuo;
  if (modo === 'medio') return breakdown.nettoMensileMedio;
  return breakdown.nettoMensilitaOrdinaria;
}

// Confronta due punti a cavallo di una discontinuita' e dice quale beneficio
// si e' perso. Nessuna soglia hardcoded: si guarda cosa e' cambiato davvero,
// cosi' la spiegazione resta valida per qualsiasi comune e configurazione.
function descriviSoglia(prima, dopo) {
  const cause = [];
  if (dopo.bonus100 < prima.bonus100) {
    cause.push('si perde il trattamento integrativo (il "bonus" da 1.200 euro)');
  }
  if (dopo.sommaIntegrativa < prima.sommaIntegrativa) {
    cause.push('cala la somma integrativa da riduzione del cuneo fiscale');
  }
  if (prima.addizionaleComunale === 0 && dopo.addizionaleComunale > 0) {
    cause.push('si supera la soglia di esenzione dell\'addizionale comunale');
  }
  if (dopo.detrazioneLavoro < prima.detrazioneLavoro) {
    cause.push('si riduce la detrazione da lavoro dipendente');
  }
  if (dopo.ulterioreDetrazione < prima.ulterioreDetrazione) {
    cause.push('cala l\'ulteriore detrazione per il cuneo fiscale');
  }
  return cause;
}

/**
 * Trova la RAL minima che produce almeno il netto richiesto.
 *
 * @param {object} data  data/data.json
 * @param {object} params  gli stessi parametri di computeNetto, senza `ral`
 * @param {{target:number, modo?:string}} richiesta
 */
export function solveRal(data, params = {}, { target, modo = 'busta' } = {}) {
  const mensilita = Number(params.mensilita) || 14;
  const netto = (ral) => computeNetto(data, { ...params, ral });
  const f = (ral) => proiettaNetto(netto(ral), modo);

  const targetAnnuo = modo === 'annuo' ? target : target * mensilita;
  const margine = modo === 'annuo'
    ? MARGINE_ANNUO
    : Math.ceil(MARGINE_ANNUO / mensilita);

  // A redditi bassi il netto puo' superare il lordo (cuneo piu' trattamento
  // integrativo): con 8.000 di RAL il netto e' 8.981. Da qui il margine sotto.
  const minimo = Math.max(0, Math.floor(targetAnnuo - 4000));
  const massimo = Math.min(RAL_MASSIMA, Math.ceil(targetAnnuo * 2.5 + 20000));

  let trovata = null;
  const valoreIniziale = f(minimo);

  if (valoreIniziale >= target) {
    trovata = minimo;
  } else {
    // Passata grossolana, fino alla prima cella che attraversa il target.
    const celle = [];
    let griglia = minimo;
    let valorePrec = valoreIniziale;
    while (griglia < massimo) {
      const successivo = Math.min(griglia + PASSO_GROSSOLANO, massimo);
      const valore = f(successivo);
      celle.push({ da: griglia, a: successivo, max: Math.max(valorePrec, valore) });
      if (valore >= target) break;
      griglia = successivo;
      valorePrec = valore;
    }

    // Raffinamento a un euro. Si riesamina l'ultima cella - quella che
    // attraversa il target - e ogni cella precedente il cui massimo agli
    // estremi resta entro il margine: e' li' che puo' nascondersi il picco
    // stretto che precede una soglia.
    for (let i = 0; i < celle.length; i++) {
      const cella = celle[i];
      if (i !== celle.length - 1 && cella.max + margine < target) continue;
      for (let ral = cella.da; ral <= cella.a; ral++) {
        if (f(ral) >= target) { trovata = ral; break; }
      }
      if (trovata !== null) break;
    }
  }

  if (trovata === null) {
    const breakdown = netto(massimo);
    return {
      ral: null,
      breakdown,
      nettoEffettivo: proiettaNetto(breakdown, modo),
      esatto: false,
      fuoriPortata: true,
      plateau: null,
      soglie: [],
      euroMarginale: null,
    };
  }

  const breakdown = netto(trovata);
  const nettoEffettivo = proiettaNetto(breakdown, modo);

  // Plateau: quante RAL diverse danno esattamente questo netto. Sapere che
  // chiedere 39.720 invece di 39.700 non cambia nulla in busta e' informazione
  // vera e utile in trattativa.
  let plateauMax = trovata;
  while (plateauMax < massimo && f(plateauMax + 1) === nettoEffettivo) plateauMax++;

  // Soglie attraversate nei 3.000 euro di RAL sotto la soluzione: servono a
  // spiegare perche' il netto richiesto costa piu' lordo del previsto.
  const soglie = [];
  let precedente = null;
  for (let ral = Math.max(0, trovata - 3000); ral <= trovata; ral += 1) {
    const corrente = netto(ral);
    const perdita = precedente ? precedente.nettoAnnuo - corrente.nettoAnnuo : 0;
    if (perdita >= PERDITA_MINIMA) {
      const cause = descriviSoglia(precedente, corrente);
      if (cause.length) soglie.push({ ral, perdita, cause });
    }
    precedente = corrente;
  }

  // Resa dell'euro lordo marginale, su 1.000 euro di RAL in piu'.
  const conMille = netto(Math.min(trovata + 1000, RAL_MASSIMA));
  const euroMarginale = {
    nettoAnnuoInPiu: conMille.nettoAnnuo - breakdown.nettoAnnuo,
    nettoInPiu: proiettaNetto(conMille, modo) - nettoEffettivo,
    resa: (conMille.nettoAnnuo - breakdown.nettoAnnuo) / 1000,
  };

  return {
    ral: trovata,
    breakdown,
    nettoEffettivo,
    esatto: nettoEffettivo === target,
    fuoriPortata: false,
    plateau: { min: trovata, max: plateauMax },
    soglie,
    euroMarginale,
  };
}
